import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const EditSalaryTemplate = ({ show, onHide, template, onTemplateUpdated }) => {
  const [formData, setFormData] = useState({
    template_id: '',
    template_name: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState('');
  const [payComponents, setPayComponents] = useState([]);
  const [selectedComponents, setSelectedComponents] = useState([]);
  const [loadingComponents, setLoadingComponents] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeComponentIndex, setActiveComponentIndex] = useState(null);
  const [activeField, setActiveField] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  const [showHelpModal, setShowHelpModal] = useState(false);

  const formulaInputRefs = useRef({});
  const valueInputRefs = useRef({});
  const API_BASE = process.env.REACT_APP_API_BASE;

  const fetchPayComponents = useCallback(async () => {
    try {
      setLoadingComponents(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/pay_components.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setPayComponents(response.data.pay_components.filter(comp => comp.is_active));
      }
    } catch (error) {
      console.error('Error fetching pay components:', error);
    } finally {
      setLoadingComponents(false);
    }
  }, [API_BASE]);

  const fetchTemplateDetails = useCallback(async () => {
    if (!template?.template_id) return;

    try {
      setFetching(true);
      setError('');
      const token = localStorage.getItem('authToken');

      // Fetch template details
      const templateResponse = await axios.get(`${API_BASE}/get_template_details.php`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { template_id: template.template_id }
      });

      if (templateResponse.data.success) {
        const templateData = templateResponse.data.template;
        const templateComponents = templateResponse.data.components || [];

        setFormData({
          template_id: templateData.template_id,
          template_name: templateData.template_name,
          description: templateData.description || ''
        });



        // Map template components with additional data
        const enhancedComponents = await Promise.all(
          templateComponents.map(async (comp) => {
            // Fetch original component details to get calculation_type and value
            try {
              const compResponse = await axios.get(`${API_BASE}/get_pay_component.php`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { component_id: comp.component_id }
              });

              if (compResponse.data.success) {
                const originalComp = compResponse.data.component;
                return {
                  ...comp,
                  original_calculation_type: originalComp.calculation_type,
                  original_value: originalComp.value,
                  component_name: originalComp.component_name,
                  component_type: originalComp.component_type
                };
              }
            } catch (error) {
              console.error('Error fetching component details:', error);
            }

            return {
              ...comp,
              original_calculation_type: comp.calculation_type,
              original_value: comp.value,
              component_name: comp.component_name || `Component ${comp.component_id}`,
              component_type: comp.component_type || 'Earning'
            };
          })
        );

        setSelectedComponents(enhancedComponents);
      } else {
        setError(templateResponse.data.error || 'Failed to fetch template details');
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to fetch template details');
      console.error('Error fetching template details:', error);
    } finally {
      setFetching(false);
    }
  }, [template, API_BASE]);

  useEffect(() => {
    if (show && template) {
      fetchTemplateDetails();
      fetchPayComponents();
    }
  }, [show, template, fetchTemplateDetails, fetchPayComponents]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleComponentSelection = (component) => {
    setSelectedComponents(prev => {
      const exists = prev.find(c => c.component_id === component.component_id);
      if (exists) {
        return prev.filter(c => c.component_id !== component.component_id);
      } else {
        const newComponent = {
          ...component,
          calculation_type: component.calculation_type || 'Fixed',
          value: component.value || '',
          component_order: prev.length + 1,
          original_calculation_type: component.calculation_type,
          original_value: component.value
        };

        const newErrors = { ...validationErrors };
        delete newErrors[`component_${component.component_id}_value`];
        setValidationErrors(newErrors);

        return [...prev, newComponent];
      }
    });
  };

  const validateValue = (calculationType, value, componentId, componentName) => {

    if (!value.trim()) {
      return `${componentName}: Value is required`;
    }

    if (calculationType === 'Fixed') {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        return `${componentName}: Please enter a valid number`;
      }
      if (numValue < 0) {
        return `${componentName}: Amount cannot be negative`;
      }
      return '';
    }

    if (calculationType === 'Percentage') {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        return `${componentName}: Please enter a valid percentage`;
      }
      if (numValue < 0 || numValue > 100) {
        return `${componentName}: Percentage must be between 0 and 100`;
      }
      return '';
    }

    if (calculationType === 'Formula') {
      if (!value.trim()) {
        return `${componentName}: Formula is required`;
      }

      const currentComponent = selectedComponents.find(c => c.component_id === componentId);
      if (currentComponent) {
        const componentNamesInFormula = value.match(/[a-zA-Z_][a-zA-Z0-9_]*/g) || [];
        const selfReference = componentNamesInFormula.some(name =>
          name.toLowerCase() === currentComponent.component_name.toLowerCase().replace(/\s+/g, '_')
        );
        if (selfReference) {
          return `${componentName}: Formula cannot reference itself`;
        }
      }
      return '';
    }

    return '';
  };

  const handleComponentConfigChange = (componentId, field, value, index) => {
    setSelectedComponents(prev =>
      prev.map(comp =>
        comp.component_id === componentId
          ? { ...comp, [field]: value }
          : comp
      )
    );

    if (field === 'calculation_type') {
      const component = selectedComponents[index];
      const error = validateValue(value, component.value || '', componentId, component.component_name);
      if (error) {
        setValidationErrors(prev => ({ ...prev, [`component_${componentId}_value`]: error }));
      } else {
        const newErrors = { ...validationErrors };
        delete newErrors[`component_${componentId}_value`];
        setValidationErrors(newErrors);
      }
    }

    if (field === 'calculation_type' && value === 'Formula') {
      setActiveComponentIndex(index);
      setActiveField('value');
      const availableComponents = payComponents
        .filter(comp => comp.component_id !== componentId)
        .map(comp => comp.component_name);
      setSuggestions(availableComponents);
      setShowSuggestions(availableComponents.length > 0);
    }
  };

  const getLastWord = (text) => {
    const operators = ['+', '-', '*', '/', '(', ')', ' ', ','];
    let lastWord = '';
    for (let i = text.length - 1; i >= 0; i--) {
      if (operators.includes(text[i])) {
        break;
      }
      lastWord = text[i] + lastWord;
    }
    return lastWord;
  };

  const handleValueChange = (componentId, value, index) => {
    setSelectedComponents(prev =>
      prev.map(comp =>
        comp.component_id === componentId
          ? { ...comp, value: value }
          : comp
      )
    );

    const component = selectedComponents[index];
    const error = validateValue(component.calculation_type, value, componentId, component.component_name);

    if (error) {
      setValidationErrors(prev => ({ ...prev, [`component_${componentId}_value`]: error }));
    } else {
      const newErrors = { ...validationErrors };
      delete newErrors[`component_${componentId}_value`];
      setValidationErrors(newErrors);
    }

    if (component.calculation_type === 'Formula') {
      if (value.length > 0) {
        const lastWord = getLastWord(value);
        if (lastWord && lastWord.length > 0) {
          const filtered = payComponents
            .filter(comp =>
              comp.component_name.toLowerCase().includes(lastWord.toLowerCase()) &&
              comp.component_id !== componentId
            )
            .map(comp => comp.component_name);

          setSuggestions(filtered);
          setShowSuggestions(filtered.length > 0);
          setActiveComponentIndex(index);
          setActiveField('value');
        } else {
          setShowSuggestions(false);
        }
      } else {
        setShowSuggestions(false);
      }
    }
  };

  const handleSuggestionClick = (suggestion) => {
    if (activeComponentIndex === null || activeField !== 'value') return;

    const component = selectedComponents[activeComponentIndex];
    const textarea = formulaInputRefs.current[component.component_id];

    if (!textarea) return;

    const value = component.value;
    const position = textarea.selectionStart;

    const textBeforeCursor = value.substring(0, position);
    const operators = ['+', '-', '*', '/', '(', ')', ' ', ','];
    let lastDelimiterIndex = -1;

    for (let i = position - 1; i >= 0; i--) {
      if (operators.includes(textBeforeCursor[i])) {
        lastDelimiterIndex = i;
        break;
      }
    }

    const newText =
      value.substring(0, lastDelimiterIndex + 1) +
      suggestion +
      value.substring(position);

    handleValueChange(component.component_id, newText, activeComponentIndex);

    setShowSuggestions(false);

    setTimeout(() => {
      if (textarea) {
        textarea.focus();
        const newPosition = lastDelimiterIndex + 1 + suggestion.length;
        textarea.setSelectionRange(newPosition, newPosition);
      }
    }, 0);
  };

  const handleFormulaInputFocus = (index, componentId) => {
    const component = selectedComponents[index];
    if (component.calculation_type === 'Formula') {
      setActiveComponentIndex(index);
      setActiveField('value');
      if (!component.value) {
        const availableComponents = payComponents
          .filter(comp => comp.component_id !== componentId)
          .map(comp => comp.component_name);
        setSuggestions(availableComponents);
        setShowSuggestions(availableComponents.length > 0);
      }
    }
  };

  const handleValueInputFocus = (index, componentId) => {
    const component = selectedComponents[index];
    if (component.calculation_type !== 'Formula') {
      setActiveComponentIndex(index);
      setActiveField('value');
    }
  };

  const insertOperator = (operator) => {
    if (activeComponentIndex === null || activeField !== 'value') return;

    const component = selectedComponents[activeComponentIndex];
    if (component.calculation_type === 'Formula') {
      const textarea = formulaInputRefs.current[component.component_id];
      if (textarea) {
        const cursorPos = textarea.selectionStart;
        const currentValue = component.value || '';
        const newValue =
          currentValue.substring(0, cursorPos) +
          operator +
          currentValue.substring(cursorPos);
        handleValueChange(component.component_id, newValue, activeComponentIndex);

        setTimeout(() => {
          textarea.focus();
          const newPosition = cursorPos + operator.length;
          textarea.setSelectionRange(newPosition, newPosition);
        }, 0);
      }
    }
  };

  const getInputPlaceholder = (calculationType) => {
    switch (calculationType) {
      case 'Fixed': return 'Enter amount (e.g., 5000)';
      case 'Percentage': return 'Enter percentage (e.g., 10)';
      case 'Formula': return 'Enter formula (e.g., basic_salary * 0.5)';
      default: return 'Enter value';
    }
  };

  const getInputType = (calculationType) => {
    switch (calculationType) {
      case 'Fixed':
      case 'Percentage':
        return 'number';
      default:
        return 'text';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!formData.template_name.trim()) {
      setError('Template name is required');
      setLoading(false);
      return;
    }

    if (selectedComponents.length === 0) {
      setError('Please select at least one pay component');
      setLoading(false);
      return;
    }

    const errors = {};
    let hasErrors = false;

    selectedComponents.forEach(comp => {
      const error = validateValue(
        comp.calculation_type,
        comp.value,
        comp.component_id,
        comp.component_name
      );
      if (error) {
        errors[`component_${comp.component_id}_value`] = error;
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setValidationErrors(errors);
      setError('Please fix the validation errors below');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const templateData = {
        template_id: formData.template_id,
        template_name: formData.template_name,
        description: formData.description,
        components: selectedComponents.map((comp, index) => ({
          component_id: comp.component_id,
          component_type: comp.component_type,
          calculation_type: comp.calculation_type,
          value: comp.value,
          component_order: index + 1
        }))
      };

      const response = await axios.post(`${API_BASE}/update_salary_template.php`,
        templateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        onTemplateUpdated();
        onHide();
        setFormData({ template_id: '', template_name: '', description: '' });
        setSelectedComponents([]);
        setValidationErrors({});
        setSuggestions([]);
        setShowSuggestions(false);
      } else {
        setError(response.data.error || 'Failed to update salary template');
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update salary template');
      console.error('Error updating salary template:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({ template_id: '', template_name: '', description: '' });
    setSelectedComponents([]);
    setValidationErrors({});
    setSuggestions([]);
    setShowSuggestions(false);
    setError('');
    onHide();
  };

  if (!show) return null;

  return (
    <>
      <div className="modal show d-block" style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1055,
        overflowY: 'auto',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        padding: '2rem 0'
      }}>
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header" style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderBottom: 'none'
            }}>
              <h5 className="modal-title text-white">
                <i className="bi bi-pencil-square me-2"></i>
                Edit Salary Template
              </h5>
              <div className="d-flex align-items-center">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-light me-3"
                  onClick={() => setShowHelpModal(true)}
                >
                  <i className="bi bi-question-circle me-1"></i>
                  Need Help?
                </button>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={handleCancel}
                  disabled={loading}
                ></button>
              </div>
            </div>

            {fetching ? (
              <div className="modal-body text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted">Loading template details...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                  {error && (
                    <div className="alert alert-danger alert-dismissible fade show" role="alert">
                      <i className="bi bi-exclamation-triangle-fill me-2"></i>
                      {error}
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setError('')}
                      ></button>
                    </div>
                  )}

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-4">
                        <h6 className="fw-bold text-primary mb-3">
                          <i className="bi bi-info-circle me-2"></i>
                          Template Information
                        </h6>

                        <div className="mb-3">
                          <label htmlFor="templateName" className="form-label fw-medium">
                            Template Name <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control border-secondary-subtle ${validationErrors.template_name ? 'is-invalid' : ''}`}
                            id="templateName"
                            name="template_name"
                            value={formData.template_name}
                            onChange={handleChange}
                            placeholder="e.g., Manager Salary, Employee Basic"
                            required
                            disabled={loading}
                          />
                          {validationErrors.template_name && (
                            <div className="invalid-feedback">{validationErrors.template_name}</div>
                          )}
                        </div>

                        <div className="mb-3">
                          <label htmlFor="description" className="form-label fw-medium">
                            Description
                          </label>
                          <textarea
                            className="form-control border-secondary-subtle"
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows="3"
                            placeholder="Describe this salary template..."
                            disabled={loading}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="fw-bold text-primary mb-0">
                            <i className="bi bi-list-check me-2"></i>
                            Selected Components ({selectedComponents.length})
                          </h6>
                          {selectedComponents.length > 0 && (
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => setSelectedComponents([])}
                              disabled={loading}
                            >
                              <i className="bi bi-trash me-1"></i>
                              Clear All
                            </button>
                          )}
                        </div>

                        {selectedComponents.length === 0 ? (
                          <div className="alert alert-info">
                            <i className="bi bi-info-circle me-2"></i>
                            No components selected. Select components from the list below.
                          </div>
                        ) : (
                          <div className="card border-0 shadow-sm">
                            <div className="card-body p-0">
                              <div className="table-responsive">
                                <table className="table table-hover align-middle mb-0">
                                  <thead className="bg-light">
                                    <tr>
                                      <th>Component</th>
                                      <th>Type</th>
                                      <th>Calculation</th>
                                      <th>Value/Formula</th>
                                      <th>Order</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {selectedComponents.map((comp, index) => {
                                      const errorKey = `component_${comp.component_id}_value`;
                                      const hasError = validationErrors[errorKey];

                                      return (
                                        <tr key={comp.component_id}>
                                          <td>
                                            <div className="d-flex align-items-center">
                                              <div className={`badge ${comp.component_type === 'Earning' ? 'bg-success' : 'bg-danger'} me-2`}>
                                                {comp.component_type === 'Earning' ? '+' : '-'}
                                              </div>
                                              <div>
                                                <div className="fw-medium">{comp.component_name}</div>
                                                <small className="text-muted">ID: {comp.component_id}</small>
                                              </div>
                                            </div>
                                          </td>
                                          <td>
                                            <span className={`badge ${comp.component_type === 'Earning' ? 'bg-success' : 'bg-danger'}`}>
                                              {comp.component_type}
                                            </span>
                                          </td>
                                          <td>
                                            <select
                                              className="form-select form-select-sm"
                                              value={comp.calculation_type}
                                              onChange={(e) => handleComponentConfigChange(comp.component_id, 'calculation_type', e.target.value, index)}
                                              disabled={loading}
                                            >
                                              <option value="Fixed">Fixed Amount</option>
                                              <option value="Percentage">Percentage</option>
                                              <option value="Formula">Custom Formula</option>
                                            </select>
                                            {comp.original_calculation_type && comp.original_calculation_type !== comp.calculation_type && (
                                              <small className="text-muted d-block mt-1">
                                                Original: {comp.original_calculation_type}
                                              </small>
                                            )}
                                          </td>
                                          <td>
                                            {comp.calculation_type === 'Formula' ? (
                                              <div className="position-relative">
                                                <textarea
                                                  ref={el => formulaInputRefs.current[comp.component_id] = el}
                                                  className={`form-control form-control-sm ${hasError ? 'is-invalid' : ''}`}
                                                  rows="2"
                                                  value={comp.value}
                                                  onChange={(e) => handleValueChange(comp.component_id, e.target.value, index)}
                                                  onFocus={() => handleFormulaInputFocus(index, comp.component_id)}
                                                  placeholder={getInputPlaceholder(comp.calculation_type)}
                                                  style={{ fontSize: '0.875rem' }}
                                                  disabled={loading}
                                                />
                                                {showSuggestions && activeComponentIndex === index && activeField === 'value' && suggestions.length > 0 && (
                                                  <div className="dropdown-menu show shadow-sm" style={{
                                                    zIndex: 1060,
                                                    maxHeight: '200px',
                                                    overflowY: 'auto'
                                                  }}>
                                                    <div className="dropdown-header small text-primary fw-medium">
                                                      <i className="bi bi-lightbulb me-1"></i>
                                                      Available Components
                                                      <small className="text-muted ms-2">(excluding current component)</small>
                                                    </div>
                                                    {suggestions.map((suggestion, idx) => (
                                                      <button
                                                        key={idx}
                                                        type="button"
                                                        className="dropdown-item py-2"
                                                        onClick={() => handleSuggestionClick(suggestion)}
                                                        disabled={loading}
                                                      >
                                                        <i className="bi bi-tag me-2" style={{ color: '#667eea' }}></i>
                                                        <span className="text-dark">{suggestion}</span>
                                                      </button>
                                                    ))}
                                                  </div>
                                                )}
                                                {hasError && (
                                                  <div className="invalid-feedback d-block">{hasError}</div>
                                                )}
                                                <div className="form-text text-muted small">
                                                  Use +, -, *, /, () operators. Start typing to see suggestions.
                                                </div>
                                              </div>
                                            ) : (
                                              <div className="position-relative">
                                                <div className="input-group input-group-sm">
                                                  {comp.calculation_type === 'Fixed' && (
                                                    <span className="input-group-text">₹</span>
                                                  )}
                                                  {comp.calculation_type === 'Percentage' && (
                                                    <span className="input-group-text">%</span>
                                                  )}
                                                  <input
                                                    ref={el => valueInputRefs.current[comp.component_id] = el}
                                                    type={getInputType(comp.calculation_type)}
                                                    className={`form-control ${hasError ? 'is-invalid' : ''}`}
                                                    value={comp.value}
                                                    onChange={(e) => handleValueChange(comp.component_id, e.target.value, index)}
                                                    onFocus={() => handleValueInputFocus(index, comp.component_id)}
                                                    placeholder={getInputPlaceholder(comp.calculation_type)}
                                                    step={comp.calculation_type === 'Percentage' ? "0.01" : "0.01"}
                                                    min={comp.calculation_type === 'Percentage' ? "0" : "0"}
                                                    max={comp.calculation_type === 'Percentage' ? "100" : ""}
                                                    disabled={loading}
                                                  />
                                                  {comp.calculation_type === 'Percentage' && (
                                                    <span className="input-group-text">%</span>
                                                  )}
                                                </div>
                                                {hasError && (
                                                  <div className="invalid-feedback d-block">{hasError}</div>
                                                )}
                                                {comp.original_value && comp.original_value !== comp.value && (
                                                  <small className="text-muted d-block mt-1">
                                                    Original: {comp.original_value}
                                                    {comp.calculation_type === 'Fixed' ? '₹' : comp.calculation_type === 'Percentage' ? '%' : ''}
                                                  </small>
                                                )}
                                              </div>
                                            )}
                                          </td>
                                          <td>
                                            <div className="btn-group btn-group-sm" role="group">
                                              <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={() => {
                                                  const newComponents = [...selectedComponents];
                                                  if (index > 0) {
                                                    [newComponents[index], newComponents[index - 1]] = [newComponents[index - 1], newComponents[index]];
                                                    setSelectedComponents(newComponents);
                                                  }
                                                }}
                                                title="Move Up"
                                                disabled={loading || index === 0}
                                              >
                                                <i className="bi bi-arrow-up"></i>
                                              </button>
                                              <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={() => {
                                                  const newComponents = [...selectedComponents];
                                                  if (index < newComponents.length - 1) {
                                                    [newComponents[index], newComponents[index + 1]] = [newComponents[index + 1], newComponents[index]];
                                                    setSelectedComponents(newComponents);
                                                  }
                                                }}
                                                title="Move Down"
                                                disabled={loading || index === selectedComponents.length - 1}
                                              >
                                                <i className="bi bi-arrow-down"></i>
                                              </button>
                                              <button
                                                type="button"
                                                className="btn btn-outline-danger"
                                                onClick={() => handleComponentSelection(comp)}
                                                title="Remove Component"
                                                disabled={loading}
                                              >
                                                <i className="bi bi-x"></i>
                                              </button>
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-12">
                      <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="fw-bold text-primary mb-0">
                            <i className="bi bi-grid-3x3-gap me-2"></i>
                            Available Pay Components
                          </h6>
                          {loadingComponents && (
                            <span className="spinner-border spinner-border-sm" role="status"></span>
                          )}
                        </div>

                        <div className="card border-0 shadow-sm">
                          <div className="card-body">
                            {payComponents.length === 0 ? (
                              <div className="text-center py-3">
                                <i className="bi bi-calculator display-4 text-muted mb-3"></i>
                                <p className="text-muted">No pay components available.</p>
                                <p className="text-muted small">Create pay components first in the Pay Components section.</p>
                              </div>
                            ) : (
                              <>
                                <div className="row">
                                  {payComponents.map(component => {
                                    const isSelected = selectedComponents.find(c => c.component_id === component.component_id);

                                    return (
                                      <div key={component.component_id} className="col-md-4 mb-3">
                                        <div
                                          className={`card component-card cursor-pointer ${isSelected ? 'border-primary bg-primary-subtle' : 'border-secondary-subtle'} ${loading ? 'opacity-50' : ''}`}
                                          onClick={() => !loading && handleComponentSelection(component)}
                                          style={{
                                            transition: 'all 0.2s',
                                            cursor: loading ? 'not-allowed' : 'pointer'
                                          }}
                                          onMouseEnter={(e) => {
                                            if (!loading) {
                                              e.currentTarget.style.transform = 'translateY(-2px)';
                                              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                                            }
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = 'none';
                                          }}
                                        >
                                          <div className="card-body p-3">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                              <div>
                                                <h6 className="card-title mb-1 fw-medium">
                                                  {component.component_name}
                                                </h6>
                                                <span className={`badge ${component.component_type === 'Earning' ? 'bg-success' : 'bg-danger'} me-1`}>
                                                  {component.component_type}
                                                </span>
                                                <span className={`badge ${component.calculation_type === 'Fixed' ? 'bg-primary' :
                                                  component.calculation_type === 'Percentage' ? 'bg-info' :
                                                    'bg-warning'
                                                  }`}>
                                                  {component.calculation_type}
                                                </span>
                                              </div>
                                              <div className="form-check">
                                                <input
                                                  type="checkbox"
                                                  className="form-check-input"
                                                  checked={isSelected}
                                                  onChange={() => { }}
                                                  readOnly
                                                />
                                              </div>
                                            </div>
                                            <div className="mt-2">
                                              <small className="text-muted d-block">
                                                <i className="bi bi-calculator me-1"></i>
                                                Value: <strong>{component.value}</strong>
                                              </small>
                                              <small className="text-muted d-block mt-1">
                                                <i className="bi bi-hash me-1"></i>
                                                ID: {component.component_id}
                                              </small>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>

                                {/* Stats and Quick Actions */}
                                <div className="mt-4 pt-4 border-top">
                                  <div className="row">
                                    <div className="col-md-6">
                                      <div className="card border-0 bg-light">
                                        <div className="card-body">
                                          <h6 className="fw-medium text-primary mb-3">
                                            <i className="bi bi-bar-chart me-2"></i>
                                            Selection Summary
                                          </h6>
                                          <div className="row">
                                            <div className="col-6">
                                              <div className="text-center">
                                                <div className="fw-bold text-success fs-4">
                                                  {selectedComponents.filter(c => c.component_type === 'Earning').length}
                                                </div>
                                                <small className="text-muted">Earnings</small>
                                              </div>
                                            </div>
                                            <div className="col-6">
                                              <div className="text-center">
                                                <div className="fw-bold text-danger fs-4">
                                                  {selectedComponents.filter(c => c.component_type === 'Deduction').length}
                                                </div>
                                                <small className="text-muted">Deductions</small>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-md-6">
                                      {selectedComponents.some(comp => comp.calculation_type === 'Formula') && (
                                        <div className="card border-0 bg-light">
                                          <div className="card-body">
                                            <h6 className="fw-medium text-primary mb-2">
                                              <i className="bi bi-magic me-2"></i>
                                              Formula Tools
                                            </h6>
                                            <div className="d-flex flex-wrap gap-2 mb-3">
                                              {['+', '-', '*', '/', '(', ')'].map(op => (
                                                <button
                                                  key={op}
                                                  type="button"
                                                  className="btn btn-outline-primary btn-sm"
                                                  onClick={() => insertOperator(op)}
                                                  title={`Insert ${op}`}
                                                  disabled={loading}
                                                >
                                                  {op}
                                                </button>
                                              ))}
                                            </div>
                                            <small className="text-muted">
                                              <i className="bi bi-info-circle me-1"></i>
                                              Click operators to insert into active formula field
                                            </small>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer" style={{
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                  borderTop: '1px solid rgba(102, 126, 234, 0.1)'
                }}>
                  <button
                    type="button"
                    className="btn btn-info text-white me-auto"
                    onClick={() => setShowHelpModal(true)}
                    style={{
                      fontWeight: '500',
                      background: '#0dcaf0',
                      border: 'none',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#0bb5d4';
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 4px 8px rgba(13, 202, 240, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#0dcaf0';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <i className="bi bi-question-circle me-2"></i>
                    Need Help?
                  </button>
                  <button
                    type="submit"
                    className="btn text-white border-0 shadow-sm me-2"
                    disabled={loading || fetching || selectedComponents.length === 0}
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      fontWeight: '500',
                      letterSpacing: '0.5px',
                      transition: 'all 0.3s',
                      padding: '0.5rem 1.5rem',
                      opacity: (loading || fetching || selectedComponents.length === 0) ? 0.7 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!loading && !fetching && selectedComponents.length > 0) {
                        e.target.style.background = 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)';
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 2px 6px rgba(102, 126, 234, 0.2)';
                    }}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-save me-2"></i>
                        Save Template
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-light border-secondary-subtle"
                    onClick={handleCancel}
                    disabled={loading}
                    style={{
                      background: 'white',
                      border: '1px solid #dee2e6',
                      color: '#6c757d',
                      transition: 'all 0.2s',
                      fontWeight: '500',
                      padding: '0.5rem 1.5rem'
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.target.style.background = '#f8f9fa';
                        e.target.style.borderColor = '#adb5bd';
                        e.target.style.color = '#495057';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'white';
                      e.target.style.borderColor = '#dee2e6';
                      e.target.style.color = '#6c757d';
                    }}
                  >
                    <i className="bi bi-x-circle me-2"></i>
                    Exit
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Help Modal */}
      {showHelpModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">
                  <i className="bi bi-question-circle me-2"></i>
                  How Salary Templates Work
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowHelpModal(false)}
                ></button>
              </div>
              <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <div className="alert alert-info">
                  <i className="bi bi-lightbulb me-2"></i>
                  <strong>Important:</strong> Salary templates calculate salaries based on employee's basic salary from salary_details table and attendance from attendance table.
                </div>

                <div className="mb-4">
                  <h6 className="fw-bold text-primary mb-3">
                    <i className="bi bi-calculator me-2"></i>
                    Calculation Types Explained
                  </h6>

                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <div className="card h-100 border-primary">
                        <div className="card-header bg-primary text-white">
                          <h6 className="mb-0">Fixed Amount</h6>
                        </div>
                        <div className="card-body">
                          <p className="card-text">
                            <strong>Fixed monthly amount</strong> regardless of attendance or other factors.
                          </p>
                          <div className="bg-light p-2 rounded">
                            <small className="text-muted">Example:</small><br />
                            <code>House Rent Allowance = ₹15,000</code><br />
                            <small>Always pays ₹15,000 every month</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-4 mb-3">
                      <div className="card h-100 border-success">
                        <div className="card-header bg-success text-white">
                          <h6 className="mb-0">Percentage</h6>
                        </div>
                        <div className="card-body">
                          <p className="card-text">
                            <strong>Percentage of employee's basic salary</strong> from salary_details table.
                          </p>
                          <div className="bg-light p-2 rounded">
                            <small className="text-muted">Example:</small><br />
                            <code>Basic Salary = 50%</code><br />
                            <small>If salary is ₹50,000 → Basic = ₹25,000</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-4 mb-3">
                      <div className="card h-100 border-warning">
                        <div className="card-header bg-warning text-dark">
                          <h6 className="mb-0">Formula</h6>
                        </div>
                        <div className="card-body">
                          <p className="card-text">
                            <strong>Dynamic calculation</strong> using other components and attendance.
                          </p>
                          <div className="bg-light p-2 rounded">
                            <small className="text-muted">Example:</small><br />
                            <code>HRA = Basic * 0.40</code><br />
                            <small>40% of Basic Salary</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <h6 className="fw-bold text-primary mb-3">
                    <i className="bi bi-clipboard-data me-2"></i>
                    Real-World Examples
                  </h6>

                  <div className="table-responsive">
                    <table className="table table-bordered">
                      <thead className="table-primary">
                        <tr>
                          <th>Component</th>
                          <th>Type</th>
                          <th>Calculation</th>
                          <th>Value</th>
                          <th>Explanation</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Basic Salary</td>
                          <td><span className="badge bg-success">Earning</span></td>
                          <td><span className="badge bg-success">Percentage</span></td>
                          <td>50%</td>
                          <td>50% of employee's total salary (from salary_details table)</td>
                        </tr>
                        <tr>
                          <td>House Rent Allowance</td>
                          <td><span className="badge bg-success">Earning</span></td>
                          <td><span className="badge bg-warning">Formula</span></td>
                          <td>Basic * 0.40</td>
                          <td>40% of Basic Salary</td>
                        </tr>
                        <tr>
                          <td>Medical Allowance</td>
                          <td><span className="badge bg-success">Earning</span></td>
                          <td><span className="badge bg-primary">Fixed</span></td>
                          <td>₹2,000</td>
                          <td>Fixed amount every month</td>
                        </tr>
                        <tr>
                          <td>Provident Fund</td>
                          <td><span className="badge bg-danger">Deduction</span></td>
                          <td><span className="badge bg-warning">Formula</span></td>
                          <td>Basic * 0.12</td>
                          <td>12% of Basic Salary</td>
                        </tr>
                        <tr>
                          <td>Professional Tax</td>
                          <td><span className="badge bg-danger">Deduction</span></td>
                          <td><span className="badge bg-primary">Fixed</span></td>
                          <td>₹200</td>
                          <td>Fixed statutory deduction</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="mb-3">
                  <h6 className="fw-bold text-primary mb-3">
                    <i className="bi bi-calendar-check me-2"></i>
                    Attendance-Based Calculations
                  </h6>
                  <div className="card bg-light">
                    <div className="card-body">
                      <p>
                        <strong>Formula components can use attendance data:</strong>
                      </p>
                      <ul>
                        <li>Daily wage = (Monthly Salary / Working Days) * Present Days</li>
                        <li>Overtime = (Basic / 240) * Overtime Hours * 2</li>
                        <li>Leave Deduction = (Basic / Working Days) * Leave Days</li>
                      </ul>
                      <div className="alert alert-warning">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        <strong>Note:</strong> Attendance data is fetched from attendance table for the payroll period.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="alert alert-success">
                  <h6 className="fw-bold mb-2">
                    <i className="bi bi-lightning me-2"></i>
                    Quick Tips
                  </h6>
                  <ul className="mb-0">
                    <li>Basic Salary is usually set as percentage (e.g., 50%)</li>
                    <li>Other allowances often depend on Basic (use formulas like Basic * 0.40)</li>
                    <li>Statutory deductions (PF, TDS) are usually formulas or fixed amounts</li>
                    <li>Always test formulas before saving template</li>
                    <li>Order matters! Calculate Basic before other dependent components</li>
                  </ul>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowHelpModal(false)}
                >
                  <i className="bi bi-check-circle me-2"></i>
                  Got it!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EditSalaryTemplate;